import { useCallback, useEffect, useState } from "react"
import { useLazyQuery } from "@apollo/client"
import SEARCH_SHOPPER_INSIGHTS from "../../graphql/queries/shopperInsights/SearchShopperInsights"
import esb from "elastic-builder"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { Query } from "lib/types/common"
import timezone from "dayjs/plugin/timezone"

dayjs.extend(utc)
dayjs.extend(timezone)

export interface ChartTimeSeriesData {
  timestamp: string
  displayLabel: string
  orders: number
  checkouts: number
  sessions: number
}

export function useChartData(params: {
  startDate: string
  endDate: string
  selectedStoreGroups: { value: string; label: string }[]
  selectedStoreMarkets: { value: string; label: string }[]
  interval: "hourly" | "daily"
  timeZone: string
  enabled?: boolean
}) {
  const {
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    interval: requestedInterval,
    timeZone,
    enabled = true
  } = params

  // Auto-determine interval based on time range
  const timeRangeHours = dayjs(endDate).diff(dayjs(startDate), "hour")
  const interval = timeRangeHours <= 24 ? "hourly" : requestedInterval

  const [chartData, setChartData] = useState<ChartTimeSeriesData[]>([])
  const [ordersOverTimeData, setOrdersOverTimeData] = useState<{ [key: string]: number }>({})
  const [checkoutsOverTimeData, setCheckoutsOverTimeData] = useState<{ [key: string]: number }>({})
  const [sessionsOverTimeData, setSessionsOverTimeData] = useState<{ [key: string]: number }>({})

  const formatLabel = (timestamp: string): string => {
    const date = dayjs(timestamp)
    if (interval === "hourly") {
      return date.format("MMM D, HH:mm")
    }
    return date.format("MMM D")
  }

  const [searchOrdersOverTime, { loading: ordersOverTimeLoading, error: ordersOverTimeError }] = useLazyQuery(
    SEARCH_SHOPPER_INSIGHTS,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data?.searchShopperInsights?.aggregations) {
          const aggregations = JSON.parse(data.searchShopperInsights.aggregations)
          const timeBuckets = aggregations?.ordersOverTime?.buckets || []

          // Bucket orders by their document creation time
          const ordersCountMap: { [key: string]: number } = {}

          timeBuckets.forEach((bucket: { key: number; key_as_string: string; uniqueOrders: { value: number } }) => {
            // Normalize timestamp to match the format we generate in useEffect
            const bucketTime = dayjs(bucket.key).tz(timeZone).startOf(interval === "hourly" ? "hour" : "day").toISOString()
            ordersCountMap[bucketTime] = bucket.uniqueOrders?.value || 0
          })

          setOrdersOverTimeData(ordersCountMap)
        }
      }
    }
  )

  const [searchCheckoutsOverTime, { loading: checkoutsOverTimeLoading, error: checkoutsOverTimeError }] = useLazyQuery(
    SEARCH_SHOPPER_INSIGHTS,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data?.searchShopperInsights?.aggregations) {
          const aggregations = JSON.parse(data.searchShopperInsights.aggregations)
          const timeBuckets = aggregations?.checkoutsOverTime?.buckets || []

          // Bucket checkouts by their document creation time (latest per session)
          const checkoutsCountMap: { [key: string]: number } = {}

          timeBuckets.forEach((bucket: { key: number; key_as_string: string; uniqueCheckouts: { value: number } }) => {
            // Normalize timestamp to match the format we generate in useEffect
            const bucketTime = dayjs(bucket.key).tz(timeZone).startOf(interval === "hourly" ? "hour" : "day").toISOString()
            checkoutsCountMap[bucketTime] = bucket.uniqueCheckouts?.value || 0
          })

          setCheckoutsOverTimeData(checkoutsCountMap)
        }
      }
    }
  )

  const [searchSessionsOverTime, { loading: sessionsOverTimeLoading, error: sessionsOverTimeError }] = useLazyQuery(
    SEARCH_SHOPPER_INSIGHTS,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data?.searchShopperInsights?.aggregations) {
          const aggregations = JSON.parse(data.searchShopperInsights.aggregations)
          const timeBuckets = aggregations?.sessionsOverTime?.buckets || []

          // Bucket sessions by their document creation time
          const sessionsCountMap: { [key: string]: number } = {}

          timeBuckets.forEach((bucket: { key: number; key_as_string: string; uniqueSessions: { value: number } }) => {
            // Normalize timestamp to match the format we generate in useEffect
            const bucketTime = dayjs(bucket.key).tz(timeZone).startOf(interval === "hourly" ? "hour" : "day").toISOString()
            sessionsCountMap[bucketTime] = bucket.uniqueSessions?.value || 0
          })

          setSessionsOverTimeData(sessionsCountMap)
        }
      }
    }
  )

  useEffect(() => {
    // Generate all time buckets in the range using the filter's timezone
    const allTimestamps: string[] = []
    const start = dayjs(startDate).tz(timeZone).startOf(interval === "hourly" ? "hour" : "day")
    const end = dayjs(endDate).tz(timeZone).startOf(interval === "hourly" ? "hour" : "day")

    let current = start
    while (current.isBefore(end) || current.isSame(end)) {
      allTimestamps.push(current.toISOString())
      current = current.add(1, interval === "hourly" ? "hour" : "day")
    }

    const merged: ChartTimeSeriesData[] = allTimestamps.map((timestamp) => ({
      timestamp,
      orders: ordersOverTimeData[timestamp] || 0,
      checkouts: checkoutsOverTimeData[timestamp] || 0,
      sessions: sessionsOverTimeData[timestamp] || 0,
      displayLabel: formatLabel(timestamp)
    }))

    setChartData(merged)
  }, [ordersOverTimeData, checkoutsOverTimeData, sessionsOverTimeData, interval, startDate, endDate, timeZone])

  const fetchTimeSeriesData = useCallback(() => {
    if (!enabled) return

    const startDateCheck = new Date(startDate)
    const startDateIsValid = startDateCheck && !isNaN(startDateCheck.getTime())
    const endDateCheck = new Date(endDate)
    const endDateIsValid = endDateCheck && !isNaN(endDateCheck.getTime())

    if (!startDateIsValid || !endDateIsValid) return

    // Orders over time query - bucket by order document creation time
    const ordersBoolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("created")
          .gte(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lte(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )
      .must(esb.existsQuery("reference"))

    if (selectedStoreGroups.length > 0) {
      ordersBoolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((option) => option.value)
        )
      )
    }

    if (selectedStoreMarkets.length > 0) {
      ordersBoolQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((option) => option.value)
        )
      )
    }

    const dateHistogramInterval = interval === "hourly" ? "1h" : "1d"
    const ordersAggs = esb
      .requestBodySearch()
      .agg(
        esb
          .dateHistogramAggregation("ordersOverTime", "created")
          .interval(dateHistogramInterval)
          .timeZone(timeZone)
          .minDocCount(0)
          .extendedBounds(dayjs(startDate).valueOf(), dayjs(endDate).valueOf())
          .agg(esb.cardinalityAggregation("uniqueOrders", "sessionId"))
      )
      .toJSON() as Query

    // Checkouts over time query - bucket by checkout document creation time (latest per session)
    const checkoutsBoolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("created")
          .gte(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lte(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )
      .must(esb.existsQuery("paymentStatus"))

    if (selectedStoreGroups.length > 0) {
      checkoutsBoolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((option) => option.value)
        )
      )
    }

    if (selectedStoreMarkets.length > 0) {
      checkoutsBoolQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((option) => option.value)
        )
      )
    }

    const checkoutsAggs = esb
      .requestBodySearch()
      .agg(
        esb
          .dateHistogramAggregation("checkoutsOverTime", "created")
          .interval(dateHistogramInterval)
          .timeZone(timeZone)
          .minDocCount(0)
          .extendedBounds(dayjs(startDate).valueOf(), dayjs(endDate).valueOf())
          .agg(esb.cardinalityAggregation("uniqueCheckouts", "sessionId"))
      )
      .toJSON() as Query

    // Sessions over time query (session documents only - not checkout or order documents)
    const sessionsBoolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("created")
          .gte(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lte(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )
      .mustNot(esb.existsQuery("paymentStatus"))
      .mustNot(esb.existsQuery("checkoutId"))

    if (selectedStoreGroups.length > 0) {
      sessionsBoolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((option) => option.value)
        )
      )
    }

    if (selectedStoreMarkets.length > 0) {
      sessionsBoolQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((option) => option.value)
        )
      )
    }

    const sessionsAggs = esb
      .requestBodySearch()
      .agg(
        esb
          .dateHistogramAggregation("sessionsOverTime", "created")
          .interval(dateHistogramInterval)
          .timeZone(timeZone)
          .minDocCount(0)
          .extendedBounds(dayjs(startDate).valueOf(), dayjs(endDate).valueOf())
          .agg(esb.cardinalityAggregation("uniqueSessions", "id"))
      )
      .toJSON() as Query

    searchOrdersOverTime({
      variables: {
        from: 0,
        size: 0,
        query: JSON.stringify(ordersBoolQuery.toJSON()),
        aggs: JSON.stringify(ordersAggs.aggs)
      },
      fetchPolicy: "no-cache"
    })

    searchCheckoutsOverTime({
      variables: {
        from: 0,
        size: 0,
        query: JSON.stringify(checkoutsBoolQuery.toJSON()),
        aggs: JSON.stringify(checkoutsAggs.aggs)
      },
      fetchPolicy: "no-cache"
    })

    searchSessionsOverTime({
      variables: {
        from: 0,
        size: 0,
        query: JSON.stringify(sessionsBoolQuery.toJSON()),
        aggs: JSON.stringify(sessionsAggs.aggs)
      },
      fetchPolicy: "no-cache"
    })
  }, [enabled, startDate, endDate, selectedStoreGroups, selectedStoreMarkets, interval, timeZone])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (enabled) {
        fetchTimeSeriesData()
      }
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [enabled, startDate, endDate, selectedStoreGroups, selectedStoreMarkets, interval, timeZone, fetchTimeSeriesData])

  return {
    chartData,
    loading: ordersOverTimeLoading || checkoutsOverTimeLoading || sessionsOverTimeLoading,
    error: ordersOverTimeError || checkoutsOverTimeError || sessionsOverTimeError,
    refetch: fetchTimeSeriesData
  }
}
